import { isApiError } from '../api/client';
import type { SocialPerson } from '../api/social';

// The lobby's INVITE panel (H10-S6b, design handoff §friends — reshaped by the user's
// follow model): TWO lists instead of one — FRIENDS (mutual follows) and FOLLOWERS —
// because D80 makes those exactly the accounts this room may invite. No presence dots:
// v1 has no presence (D82) and a dot that always says offline is a lie. A guest sees
// an honest sign-in note, never an empty panel pretending to be a feature.
// ui/ consumes a structural port (T-UI-020): the api object is injected, never imported.

export interface SocialPanelPort {
  friends(): Promise<SocialPerson[]>;
  followers(): Promise<SocialPerson[]>;
  invite(accountId: string, roomCode: string): Promise<void>;
}

export interface SocialPanelOpts {
  roomCode: string;
  /** null ⇒ not signed in — render the note, fetch nothing. */
  signedIn: boolean;
}

export interface SocialPanel {
  readonly el: HTMLElement;
  dispose(): void;
}

export function mountSocialPanel(host: HTMLElement, port: SocialPanelPort, opts: SocialPanelOpts): SocialPanel {
  const doc = host.ownerDocument;
  const el = doc.createElement('div');
  el.className = 'lobby-panel social-panel';
  host.appendChild(el);
  let disposed = false;
  const invited = new Set<string>();

  const caps = (text: string): HTMLElement => {
    const l = doc.createElement('div');
    l.className = 'caps-label';
    l.textContent = text;
    return l;
  };

  const note = (text: string): HTMLElement => {
    const n = doc.createElement('div');
    n.className = 'social-note';
    n.textContent = text;
    return n;
  };

  const row = (p: SocialPerson): HTMLElement => {
    const r = doc.createElement('div');
    r.className = 'social-row';
    r.dataset['socialRow'] = p.id;
    const name = doc.createElement('div');
    name.className = 'social-name';
    name.textContent = p.displayName;
    const btn = doc.createElement('button');
    btn.className = 'social-invite';
    btn.dataset['socialInvite'] = p.id;
    const done = invited.has(p.id);
    btn.textContent = done ? 'Invited ✓' : 'Invite';
    btn.disabled = done;
    btn.addEventListener('click', () => {
      btn.disabled = true; // no double-fire while the request is out
      port
        .invite(p.id, opts.roomCode)
        .then(() => {
          if (disposed) return;
          invited.add(p.id);
          btn.textContent = 'Invited ✓';
        })
        .catch((e: unknown) => {
          if (disposed) return;
          btn.disabled = false;
          showError(isApiError(e) ? e.message : 'could not send the invite');
        });
    });
    r.append(name, btn);
    return r;
  };

  const errorEl = doc.createElement('div');
  errorEl.className = 'social-error';
  errorEl.dataset['socialError'] = '1';
  const showError = (text: string): void => {
    errorEl.textContent = text;
  };

  const section = (label: string, people: SocialPerson[], emptyText: string): HTMLElement => {
    const wrap = doc.createElement('div');
    wrap.className = 'social-section';
    wrap.appendChild(caps(label));
    if (people.length === 0) {
      wrap.appendChild(note(emptyText));
      return wrap;
    }
    for (const p of people) wrap.appendChild(row(p));
    return wrap;
  };

  const render = async (): Promise<void> => {
    el.textContent = '';
    el.appendChild(caps('INVITE PLAYERS'));
    if (!opts.signedIn) {
      el.appendChild(note('sign in to follow players and invite them to your rooms'));
      return;
    }
    el.appendChild(note('loading…'));
    try {
      const [friends, followers] = await Promise.all([port.friends(), port.followers()]);
      if (disposed) return;
      el.textContent = '';
      el.appendChild(caps('INVITE PLAYERS'));
      // a friend is also a follower — the friends list wins the row, the followers
      // list shows only the rest, so nobody appears (and gets invited) twice
      const friendIds = new Set(friends.map((f) => f.id));
      const followersOnly = followers.filter((f) => !friendIds.has(f.id));
      el.appendChild(section('FRIENDS', friends, 'no friends yet — mutual follows land here'));
      el.appendChild(section('FOLLOWERS', followersOnly, 'nobody follows you yet'));
      el.appendChild(errorEl);
    } catch (e: unknown) {
      if (disposed) return;
      el.textContent = '';
      el.appendChild(caps('INVITE PLAYERS'));
      el.appendChild(note(isApiError(e) ? e.message : 'could not load your lists'));
    }
  };

  void render();
  return {
    el,
    dispose(): void {
      disposed = true;
      el.remove();
    },
  };
}
